/**
 * RegionSettings — Account → "Country & currency" on the web.
 *
 * I let a member say where they live and which currency their money is in.
 * Picking a country offers that country's currencies and time zones (from
 * /auth/regions/, CLDR data) and pre-selects sensible ones; Save sends
 * country, home_currency, locale and time_zone to /auth/me and reloads the
 * account, so every amount on every surface re-reads in the new currency.
 * Changing the currency changes how amounts are SHOWN; it never converts
 * amounts already entered, and the screen says so. The SARS tax pack stays
 * South African only.
 *
 * Author: Maré Greyling (Thevertex) <mare.greyling@gmail.com> — https://mare-greyling.com
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api.js';
import { useAuth } from '../App.jsx';
import { countryOptions } from '../global.js';

function currencyName(code) {
  try { return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) || code; } catch { return code; }
}

function sample(currency, locale) {
  try { return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(1234.5); } catch { return `${currency} 1234.50`; }
}

function browserZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

export default function RegionSettings() {
  const { me, reload } = useAuth();
  const countries = useMemo(() => countryOptions('en'), []);
  const [country, setCountry] = useState(me?.country || 'ZA');
  const [currency, setCurrency] = useState(me?.home_currency || 'ZAR');
  const [locale, setLocale] = useState(me?.locale || 'en-ZA');
  const [zone, setZone] = useState(me?.time_zone || 'Africa/Johannesburg');
  const [region, setRegion] = useState(null);      // the chosen country's defaults
  const [allCurrencies, setAllCurrencies] = useState([]);
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api('/auth/regions/').then((d) => setAllCurrencies((d.currencies || []).map((c) => (typeof c === 'string' ? c : c.code)))).catch(() => {});
  }, []);

  // When the country changes, offer that country's currencies and zones.
  useEffect(() => {
    let live = true;
    api(`/auth/regions/?country=${country}`).then((d) => {
      if (!live) return;
      setRegion(d);
      if (country !== (me?.country || 'ZA')) {
        setCurrency((c) => ((d.currencies || []).includes(c) ? c : d.default_currency));
        const bz = browserZone();
        setZone((z) => ((d.time_zones || []).includes(z) ? z : ((d.time_zones || []).includes(bz) ? bz : d.default_time_zone)));
        setLocale(d.default_locale || 'en');
      }
    }).catch(() => setRegion(null));
    return () => { live = false; };
  }, [country, me?.country]);

  const localCurrencies = region?.currencies || [];
  const otherCurrencies = allCurrencies.filter((c) => !localCurrencies.includes(c));
  const zones = Array.from(new Set([...(region?.time_zones || []), zone, browserZone()].filter(Boolean)));
  const changed = country !== me?.country || currency !== me?.home_currency
    || zone !== me?.time_zone || locale !== me?.locale;

  const save = async () => {
    setBusy(true); setMsg('');
    try {
      await api('/auth/me/', { method: 'PATCH', body: { country, home_currency: currency, locale, time_zone: zone } });
      await reload();
      setMsg('Saved. Every amount now shows in this currency.');
    } catch (e) {
      const d = e?.data || {};
      setMsg(d.country?.[0] || d.home_currency?.[0] || d.time_zone?.[0] || d.locale?.[0] || d.detail || 'Could not save — try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3 mt-1">
      <label className="block text-sm">
        <span className="block text-[11px] text-slate-500 mb-1">Country</span>
        <select className="input" value={country} onChange={(e) => setCountry(e.target.value)} aria-label="Country">
          {countries.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        <span className="block text-[11px] text-slate-500 mb-1">Currency your amounts are in</span>
        <select className="input" value={currency} onChange={(e) => setCurrency(e.target.value)} aria-label="Currency">
          {localCurrencies.length > 0 && (
            <optgroup label="Used in this country">
              {localCurrencies.map((c) => <option key={c} value={c}>{c} · {currencyName(c)}</option>)}
            </optgroup>
          )}
          <optgroup label="All currencies">
            {(otherCurrencies.length ? otherCurrencies : [currency]).map((c) => <option key={c} value={c}>{c} · {currencyName(c)}</option>)}
          </optgroup>
        </select>
      </label>
      <label className="block text-sm">
        <span className="block text-[11px] text-slate-500 mb-1">Time zone</span>
        <select className="input" value={zone} onChange={(e) => setZone(e.target.value)} aria-label="Time zone">
          {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </select>
      </label>
      <p className="text-xs text-slate-400">
        Amounts will read like <b className="text-slate-200">{sample(currency, locale)}</b>. Changing the
        currency changes how amounts are shown; it does not convert amounts you have already entered.
        {country !== 'ZA' && ' The tax pack (SARS) is only for South African accounts, so it is hidden for this country.'}
      </p>
      <div className="flex items-center gap-3">
        <button className="btn-primary !py-2 !px-4 text-sm" disabled={!changed || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        {msg && <span className="text-xs text-slate-400" role="status">{msg}</span>}
      </div>
    </div>
  );
}
